import { computed, Directive, effect, ElementRef, inject, input, Renderer2, signal } from '@angular/core';
import { SpinnerService } from '../services/spinner.service';

/**
 * Shows a small spinner on the host when its id is in SpinnerService.activeButtonIds.
 * Use with API calls that pass the same buttonId and showLoader: true.
 *
 * @example
 * <button [appBtnLoader]="'deal-submit-approval'" (click)="submit()">Submit</button>
 */
@Directive({
  selector: '[appBtnLoader]',
  standalone: true,
  host: {
    '[attr.aria-busy]': 'isLoading() || null',
    '[class.pe-none]': 'isLoading()'
  }
})
export class BtnLoaderDirective {
  readonly appBtnLoader = input.required<string>();

  readonly #spinner = inject(SpinnerService);
  readonly #el = inject(ElementRef<HTMLElement>);
  readonly #renderer = inject(Renderer2);

  readonly isLoading = computed(() => this.#spinner.isButtonLoading(this.appBtnLoader()));

  readonly #spinnerEl = signal<HTMLElement | null>(null);
  /** True while this directive applied disabled for the current loading cycle. */
  readonly #disabledByLoader = signal(false);
  /** Whether the host was already disabled before this loading cycle. */
  readonly #wasDisabled = signal(false);

  constructor() {
    effect(() => {
      const loading = this.isLoading();
      const host = this.#el.nativeElement;
      const isButton = host.tagName === 'BUTTON' || host.getAttribute('role') === 'button';

      if (loading) {
        // Only capture the pre-load disabled state once per loading cycle.
        // Creating the spinner updates #spinnerEl and re-runs this effect; without the
        // guard we would see our own disabled attribute and never clear it afterwards.
        if (isButton && !this.#disabledByLoader()) {
          this.#wasDisabled.set(host.hasAttribute('disabled'));
          this.#disabledByLoader.set(true);
          if (!this.#wasDisabled()) {
            this.#renderer.setAttribute(host, 'disabled', 'true');
          }
        }

        if (!this.#spinnerEl()) {
          const spinner = this.#renderer.createElement('span');
          this.#renderer.addClass(spinner, 'spinner-border');
          this.#renderer.addClass(spinner, 'spinner-border-sm');
          this.#renderer.setAttribute(spinner, 'role', 'status');
          this.#renderer.setAttribute(spinner, 'aria-hidden', 'true');

          if (isButton) {
            this.#renderer.addClass(spinner, 'me-2');
            this.#renderer.insertBefore(host, spinner, host.firstChild);
          } else {
            this.#renderer.addClass(host, 'position-relative');
            this.#renderer.addClass(spinner, 'position-absolute');
            this.#renderer.addClass(spinner, 'top-0');
            this.#renderer.addClass(spinner, 'end-0');
            this.#renderer.addClass(spinner, 'mt-1');
            this.#renderer.addClass(spinner, 'me-1');
            this.#renderer.setStyle(spinner, 'z-index', '2');
            this.#renderer.appendChild(host, spinner);
          }

          this.#spinnerEl.set(spinner);
        }
      } else {
        const spinner = this.#spinnerEl();
        if (spinner) {
          this.#renderer.removeChild(host, spinner);
          this.#spinnerEl.set(null);
        }
        if (isButton && this.#disabledByLoader()) {
          if (!this.#wasDisabled()) {
            this.#renderer.removeAttribute(host, 'disabled');
          }
          this.#disabledByLoader.set(false);
        }
      }
    });
  }
}
